import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { GASTOS_FIXOS, GASTOS_VARIAVEIS, ENTRADAS, CARTOES, OBJETIVOS, INVESTIMENTOS } from "./mockData";
import { useAuth } from "../../../contexts/AuthContext";

const STORAGE_KEY_PREFIX = "monyx_app_data_";
const LEGACY_STORAGE_KEY = "monyx_app_data_v1";
const getStorageKey = (userId) => userId ? `${STORAGE_KEY_PREFIX}${userId}` : null;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Ctx = createContext(null);
const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};
export const useAppData = () => useContext(Ctx);

const SEED = {
  gastosFixos: GASTOS_FIXOS,
  gastosVariaveis: GASTOS_VARIAVEIS,
  entradas: ENTRADAS,
  cartoes: CARTOES,
  objetivos: OBJETIVOS,
  investimentos: INVESTIMENTOS,
  listas: [],
  selectedMonth: getCurrentMonth(),
};

const EMPTY = {
  gastosFixos: [],
  gastosVariaveis: [],
  entradas: [],
  cartoes: [],
  objetivos: [],
  investimentos: { resumo: { custo: 0, patrimonio: 0, rentabilidade: 0 }, acoes: [], cdi: [], cryptos: [] },
  customCategories: [],
  listas: [],
  selectedMonth: getCurrentMonth(),
};

const DEDUP_KEYS = ["gastosFixos", "gastosVariaveis", "entradas"];

const deduplicateState = (state) => {
  const result = { ...state };
  DEDUP_KEYS.forEach((k) => {
    if (!Array.isArray(result[k])) return;
    const seen = new Set();
    result[k] = result[k].filter((it) => {
      const key = `${(it.desc || "").toLowerCase().trim()}|${it.data}|${Number(it.valor)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  });
  return result;
};

const loadFromLocalStorage = (key) => {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (raw) return deduplicateState({ ...EMPTY, ...JSON.parse(raw) });
  } catch { /* ignore */ }
  return null;
};

const uid = (prefix = "x") => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const AppDataProvider = ({ children }) => {
  const auth = useAuth();
  const userId = auth?.user?.user_id;
  const [data, setData] = useState(EMPTY);
  const [cloudReady, setCloudReady] = useState(false);
  const saveTimer = useRef(null);

  // Load from cloud once user is known
  useEffect(() => {
    if (!userId) { setData(EMPTY); setCloudReady(false); return; }
    const storageKey = getStorageKey(userId);

    // Migrate legacy localStorage to per-user key (one-time)
    const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacyRaw && storageKey) {
      try {
        if (!localStorage.getItem(storageKey)) {
          localStorage.setItem(storageKey, legacyRaw);
        }
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      } catch { /* ignore */ }
    }

    // Load from per-user localStorage immediately (instant cache)
    const localData = loadFromLocalStorage(storageKey);
    if (localData) setData({ ...localData, selectedMonth: getCurrentMonth() });

    let cancelled = false;
    (async () => {
      try {
        const { data: rec } = await axios.get(`${API}/me/data`, { withCredentials: true });
        if (cancelled) return;
        if (rec.state && Object.keys(rec.state).length > 0) {
          setData({ ...deduplicateState({ ...EMPTY, ...rec.state }), selectedMonth: getCurrentMonth() });
        } else if (!localData) {
          setData(EMPTY);
        }
        setCloudReady(true);
      } catch {
        setCloudReady(true); // keep local fallback
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Persist: per-user localStorage immediately, cloud debounced
  useEffect(() => {
    const storageKey = getStorageKey(userId);
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify(data)); } catch { /* */ }
    }
    if (!cloudReady || !userId) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      axios.put(`${API}/me/data`, { state: data }, { withCredentials: true }).catch(() => { /* offline */ });
    }, 700);
  }, [data, cloudReady, userId]);

  // Cross-tab sync (same browser, different tabs)
  useEffect(() => {
    const storageKey = getStorageKey(userId);
    if (!storageKey) return;
    const onStorage = (e) => {
      if (e.key === storageKey && e.newValue) {
        try { setData((prev) => ({ ...prev, ...JSON.parse(e.newValue) })); } catch { /* */ }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [userId]);

  /* ---------- Mês selecionado ---------- */
  const setSelectedMonth = useCallback((ym) => {
    setData((d) => ({ ...d, selectedMonth: ym }));
  }, []);

  /* ---------- Lançamentos (fixos / variáveis / entradas) ---------- */
  const tipoToKey = (tipo) => ({ fixo: "gastosFixos", variavel: "gastosVariaveis", entrada: "entradas" }[tipo]);

  const replicateMonthly = (item, fromYM, count = 12) => {
    const out = [];
    const seriesId = item.seriesId || uid("s");
    const day = parseInt((item.data || "").slice(8, 10), 10) || 15;
    const startYear = parseInt(fromYM.split("-")[0], 10);
    const startMonth = parseInt(fromYM.split("-")[1], 10) - 1;
    for (let i = 0; i < count; i++) {
      const y = startYear + Math.floor((startMonth + i) / 12);
      const m = (startMonth + i) % 12;
      const lastDay = new Date(y, m + 1, 0).getDate();
      const d = Math.min(day, lastDay);
      const dataISO = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      out.push({ ...item, id: uid(item.tipoOrigin || "x"), seriesId, data: dataISO, pago: false });
    }
    return out;
  };

  const addLancamento = useCallback((tipo, item) => {
    const key = tipoToKey(tipo);
    if (item.repetir) {
      const ym = (item.data || "").slice(0, 7);
      let count = 12;
      if (item.repetirAte) {
        const [sy, sm] = ym.split("-").map(Number);
        const [ey, em] = item.repetirAte.split("-").map(Number);
        count = (ey - sy) * 12 + (em - sm) + 1;
        count = Math.max(1, Math.min(count, 36));
      }
      const novos = replicateMonthly({ ...item, tipoOrigin: tipo[0] }, ym, count).map((x, i) => i === 0 ? { ...x, pago: !!item.pago } : x);
      setData((d) => ({ ...d, [key]: [...novos, ...d[key]], selectedMonth: ym || d.selectedMonth }));
      return novos[0];
    }
    const novo = { id: uid(tipo[0]), pago: false, ...item };
    const ym = (item.data || "").slice(0, 7);
    setData((d) => ({ ...d, [key]: [novo, ...d[key]], selectedMonth: ym || d.selectedMonth }));
    return novo;
  }, []);

  const updateLancamento = useCallback((tipo, id, patch) => {
    const key = tipoToKey(tipo);
    setData((d) => {
      const updated = d[key].map((x) => (x.id === id ? { ...x, ...patch } : x));
      // If user changed the date to a different month, auto-jump to that month so they see the edit
      const newYM = (patch.data || "").slice(0, 7);
      const sel = newYM && newYM !== d.selectedMonth ? newYM : d.selectedMonth;
      return { ...d, [key]: updated, selectedMonth: sel };
    });
  }, []);

  const removeLancamento = useCallback((tipo, id) => {
    const key = tipoToKey(tipo);
    setData((d) => ({ ...d, [key]: d[key].filter((x) => x.id !== id) }));
  }, []);

  const removeLancamentoSerie = useCallback((tipo, seriesId) => {
    const key = tipoToKey(tipo);
    setData((d) => ({ ...d, [key]: d[key].filter((x) => x.seriesId !== seriesId) }));
  }, []);

  // Updates shared fields across all series items except the one being edited.
  // If newDay is provided, updates the day part of each item's date preserving its month/year.
  const updateLancamentoSerie = useCallback((tipo, seriesId, currentId, patch, newDay) => {
    const key = tipoToKey(tipo);
    setData((d) => ({
      ...d,
      [key]: d[key].map((x) => {
        if (x.seriesId !== seriesId || x.id === currentId) return x;
        const updated = { ...x, ...patch };
        if (newDay && x.data) {
          const [y, m] = x.data.split("-");
          const lastDay = new Date(parseInt(y, 10), parseInt(m, 10), 0).getDate();
          const day = Math.min(parseInt(newDay, 10), lastDay);
          updated.data = `${y}-${m}-${String(day).padStart(2, "0")}`;
        }
        return updated;
      }),
    }));
  }, []);

  // Extends a series by adding new monthly items from (fromYM + 1 month) up to toYM inclusive.
  const extendLancamentoSerie = useCallback((tipo, seriesId, fromYM, toYM, template) => {
    const key = tipoToKey(tipo);
    const [fy, fm] = fromYM.split("-").map(Number);
    const [ty, tm] = toYM.split("-").map(Number);
    const monthsToAdd = (ty - fy) * 12 + (tm - fm);
    if (monthsToAdd <= 0) return;
    const day = parseInt((template.data || "").slice(8, 10), 10) || 15;
    const novos = [];
    for (let i = 1; i <= monthsToAdd; i++) {
      const rawMonth = fm + i;
      const y = fy + Math.floor((rawMonth - 1) / 12);
      const m = ((rawMonth - 1) % 12) + 1;
      const lastDay = new Date(y, m, 0).getDate();
      const d = Math.min(day, lastDay);
      const dataISO = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      novos.push({ ...template, id: uid(template.tipoOrigin || "x"), seriesId, data: dataISO, pago: false });
    }
    setData((d) => ({ ...d, [key]: [...d[key], ...novos] }));
  }, []);

  // Removes all series items whose month is strictly after endYM.
  const truncateLancamentoSerie = useCallback((tipo, seriesId, endYM) => {
    const key = tipoToKey(tipo);
    setData((d) => ({
      ...d,
      [key]: d[key].filter((x) => x.seriesId !== seriesId || (x.data || "").slice(0, 7) <= endYM),
    }));
  }, []);

  // Removes duplicate entries (same desc+data+valor) keeping the first occurrence
  const deduplicateLancamentos = useCallback((tipo) => {
    const key = tipoToKey(tipo);
    setData((d) => {
      const seen = new Set();
      const deduped = d[key].filter((it) => {
        const k = `${(it.desc || "").toLowerCase().trim()}|${it.data}|${Number(it.valor)}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      if (deduped.length === d[key].length) return d;
      return { ...d, [key]: deduped };
    });
  }, []);

  /* ---------- Cartões ---------- */
  const addCartao = useCallback((cartao) => {
    const novo = { id: uid("c"), atual: 0, pendente: 0, futuro: 0, transacoes: [], futuras: [], faturasPagas: {}, ...cartao };
    setData((d) => ({ ...d, cartoes: [...d.cartoes, novo] }));
    return novo;
  }, []);

  const updateCartao = useCallback((id, patch) => {
    setData((d) => ({ ...d, cartoes: d.cartoes.map((c) => (c.id === id ? { ...c, ...patch } : c)) }));
  }, []);

  const removeCartao = useCallback((id) => {
    setData((d) => ({ ...d, cartoes: d.cartoes.filter((c) => c.id !== id) }));
  }, []);

  const addCartaoTx = useCallback((cartaoId, tx) => {
    // Detect parcel pattern N/M and replicate across months
    const m = String(tx.parcelas || "1/1").match(/^(\d+)\/(\d+)$/);
    const total = m ? parseInt(m[2], 10) : 1;
    if (total > 1) {
      const baseDate = new Date(tx.data + "T00:00:00");
      const day = baseDate.getDate();
      const valorParcela = Number(tx.valor);
      const novas = [];
      for (let i = 0; i < total; i++) {
        const ny = baseDate.getFullYear() + Math.floor((baseDate.getMonth() + i) / 12);
        const nm = (baseDate.getMonth() + i) % 12;
        const lastDay = new Date(ny, nm + 1, 0).getDate();
        const d = Math.min(day, lastDay);
        const dataISO = `${ny}-${String(nm + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        novas.push({ id: uid("t"), ...tx, data: dataISO, valor: valorParcela, parcelas: `${i + 1}/${total}` });
      }
      setData((d) => ({
        ...d,
        cartoes: d.cartoes.map((c) =>
          c.id === cartaoId ? { ...c, transacoes: [...novas, ...c.transacoes] } : c
        ),
      }));
      return novas[0];
    }
    const novo = { id: uid("t"), parcelas: "1/1", proxima: "—", ...tx };
    setData((d) => ({
      ...d,
      cartoes: d.cartoes.map((c) =>
        c.id === cartaoId ? { ...c, transacoes: [novo, ...c.transacoes] } : c
      ),
    }));
    return novo;
  }, []);

  const updateCartaoTx = useCallback((cartaoId, txId, patch) => {
    setData((d) => ({
      ...d,
      cartoes: d.cartoes.map((c) =>
        c.id === cartaoId
          ? { ...c, transacoes: c.transacoes.map((t) => (t.id === txId ? { ...t, ...patch } : t)) }
          : c
      ),
    }));
  }, []);

  const removeCartaoTx = useCallback((cartaoId, txId, opts = {}) => {
    setData((d) => ({
      ...d,
      cartoes: d.cartoes.map((c) => {
        if (c.id !== cartaoId) return c;
        const tx = c.transacoes.find((t) => t.id === txId);
        if (!tx) return c;
        // Se removeSerie=true, apaga TODAS as parcelas com mesma descrição+valor (mesma compra)
        if (opts.removeSerie) {
          const m = String(tx.parcelas || "1/1").match(/^\d+\s*\/\s*(\d+)$/);
          const totalParc = m ? m[1] : null;
          const filtered = c.transacoes.filter((t) => {
            if (t.id === txId) return false;
            if (t.desc !== tx.desc) return true;
            if (Number(t.valor) !== Number(tx.valor)) return true;
            // mesma série: mesmo total de parcelas (ex: "X/6")
            const tm = String(t.parcelas || "1/1").match(/^\d+\s*\/\s*(\d+)$/);
            const tTotal = tm ? tm[1] : null;
            if (totalParc && tTotal && totalParc === tTotal) return false;
            return true;
          });
          return { ...c, transacoes: filtered };
        }
        return { ...c, transacoes: c.transacoes.filter((t) => t.id !== txId) };
      }),
    }));
  }, []);

  const importCartaoCSV = useCallback((cartaoId, transacoesParsed) => {
    // Expande parcelas N/M em N transações (uma por mês consecutivo, mesmo dia)
    const expandidas = [];
    transacoesParsed.forEach((tx) => {
      const m = String(tx.parcelas || "1/1").match(/^(\d+)\s*\/\s*(\d+)$/);
      const cur = m ? parseInt(m[1], 10) : 1;
      let total = m ? parseInt(m[2], 10) : 1;
      if (total < cur) total = cur; // guarda malformado tipo "2/0"
      const valor = Number(tx.valor) || 0;
      const baseDate = new Date((tx.data || new Date().toISOString().slice(0, 10)) + "T00:00:00");
      const day = baseDate.getDate();
      const remaining = Math.max(1, total - cur + 1);
      for (let i = 0; i < remaining; i++) {
        const ny = baseDate.getFullYear() + Math.floor((baseDate.getMonth() + i) / 12);
        const nm = (baseDate.getMonth() + i) % 12;
        const lastDay = new Date(ny, nm + 1, 0).getDate();
        const d = Math.min(day, lastDay);
        const dataISO = `${ny}-${String(nm + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        expandidas.push({
          id: uid("t"),
          desc: tx.desc,
          valor,
          data: dataISO,
          cat: tx.cat || "eventuais",
          parcelas: total > 1 ? `${cur + i}/${total}` : "1/1",
          proxima: "—",
        });
      }
    });
    setData((d) => ({
      ...d,
      cartoes: d.cartoes.map((c) =>
        c.id === cartaoId
          ? { ...c, transacoes: [...expandidas, ...c.transacoes] }
          : c
      ),
    }));
    return expandidas.length;
  }, []);

  // Import transactions directly without expanding parcels (used by PDF import)
  // All transactions are added as-is, keeping their dates in the billing month
  const addCartaoTxDirect = useCallback((cartaoId, transactions) => {
    const items = transactions.map((tx) => ({
      id: uid("t"),
      desc: tx.desc || "",
      valor: Number(tx.valor) || 0,
      data: tx.data || new Date().toISOString().slice(0, 10),
      cat: tx.cat || "eventuais",
      parcelas: tx.parcelas || "1/1",
      proxima: "\u2014",
    }));
    setData((d) => ({
      ...d,
      cartoes: d.cartoes.map((c) =>
        c.id === cartaoId
          ? { ...c, transacoes: [...items, ...c.transacoes] }
          : c
      ),
    }));
    return items.length;
  }, []);

  /* ---------- Objetivos ---------- */
  const addObjetivo = useCallback((o) => {
    const novo = { id: uid("o"), atual: 0, status: "No caminho certo", prazo: "—", ...o };
    setData((d) => ({ ...d, objetivos: [...d.objetivos, novo] }));
    return novo;
  }, []);

  const updateObjetivo = useCallback((id, patch) => {
    setData((d) => ({ ...d, objetivos: d.objetivos.map((o) => (o.id === id ? { ...o, ...patch } : o)) }));
  }, []);

  const removeObjetivo = useCallback((id) => {
    setData((d) => ({ ...d, objetivos: d.objetivos.filter((o) => o.id !== id) }));
  }, []);

  /* ---------- Investimentos ---------- */
  const addAcao = useCallback((a) => {
    const novo = { ...a, custoTotal: a.qtd * a.pmc, patrimonio: a.qtd * a.cotacao, lucro: a.qtd * (a.cotacao - a.pmc), variacao: a.pmc ? ((a.cotacao - a.pmc) / a.pmc) * 100 : 0 };
    setData((d) => ({ ...d, investimentos: { ...d.investimentos, acoes: [...d.investimentos.acoes, novo] } }));
  }, []);

  const updateAcao = useCallback((ticker, patch) => {
    setData((d) => ({
      ...d,
      investimentos: {
        ...d.investimentos,
        acoes: d.investimentos.acoes.map((a) => {
          if (a.ticker !== ticker) return a;
          const merged = { ...a, ...patch };
          merged.custoTotal = merged.qtd * merged.pmc;
          merged.patrimonio = merged.qtd * merged.cotacao;
          merged.lucro = merged.qtd * (merged.cotacao - merged.pmc);
          merged.variacao = merged.pmc ? ((merged.cotacao - merged.pmc) / merged.pmc) * 100 : 0;
          return merged;
        }),
      },
    }));
  }, []);

  const removeAcao = useCallback((ticker) => {
    setData((d) => ({ ...d, investimentos: { ...d.investimentos, acoes: d.investimentos.acoes.filter((a) => a.ticker !== ticker) } }));
  }, []);

  const addCdi = useCallback((c) => {
    setData((d) => ({ ...d, investimentos: { ...d.investimentos, cdi: [...d.investimentos.cdi, c] } }));
  }, []);

  const removeCdi = useCallback((idx) => {
    setData((d) => ({ ...d, investimentos: { ...d.investimentos, cdi: d.investimentos.cdi.filter((_, i) => i !== idx) } }));
  }, []);

  const addCrypto = useCallback((c) => {
    const novo = { id: uid("cr"), cotacao: 0, ...c };
    setData((d) => ({ ...d, investimentos: { ...d.investimentos, cryptos: [...(d.investimentos.cryptos || []), novo] } }));
  }, []);

  const updateCrypto = useCallback((id, patch) => {
    setData((d) => ({
      ...d,
      investimentos: {
        ...d.investimentos,
        cryptos: (d.investimentos.cryptos || []).map((c) => (c.id === id ? { ...c, ...patch } : c)),
      },
    }));
  }, []);

  const removeCrypto = useCallback((id) => {
    setData((d) => ({ ...d, investimentos: { ...d.investimentos, cryptos: (d.investimentos.cryptos || []).filter((c) => c.id !== id) } }));
  }, []);

  /* ---------- Listas de Compras ---------- */
  const addLista = useCallback((lista) => {
    const novo = { id: uid("l"), status: "aberta", itens: [], gastoVariavelId: null, data: new Date().toISOString().slice(0, 10), ...lista };
    setData((d) => ({ ...d, listas: [novo, ...(d.listas || [])] }));
    return novo;
  }, []);

  const updateLista = useCallback((id, patch) => {
    setData((d) => ({ ...d, listas: (d.listas || []).map((l) => (l.id === id ? { ...l, ...patch } : l)) }));
  }, []);

  const removeLista = useCallback((id) => {
    setData((d) => ({ ...d, listas: (d.listas || []).filter((l) => l.id !== id) }));
  }, []);

  const addItemLista = useCallback((listaId, item) => {
    const novo = { id: uid("li"), qtd: 1, valorUnit: 0, checked: false, ...item };
    setData((d) => ({
      ...d,
      listas: (d.listas || []).map((l) =>
        l.id === listaId ? { ...l, itens: [...(l.itens || []), novo] } : l
      ),
    }));
    return novo;
  }, []);

  const updateItemLista = useCallback((listaId, itemId, patch) => {
    setData((d) => ({
      ...d,
      listas: (d.listas || []).map((l) =>
        l.id === listaId
          ? { ...l, itens: (l.itens || []).map((it) => (it.id === itemId ? { ...it, ...patch } : it)) }
          : l
      ),
    }));
  }, []);

  const removeItemLista = useCallback((listaId, itemId) => {
    setData((d) => ({
      ...d,
      listas: (d.listas || []).map((l) =>
        l.id === listaId ? { ...l, itens: (l.itens || []).filter((it) => it.id !== itemId) } : l
      ),
    }));
  }, []);

  const finalizarLista = useCallback((listaId, gastoVariavelId) => {
    setData((d) => ({
      ...d,
      listas: (d.listas || []).map((l) =>
        l.id === listaId ? { ...l, status: "finalizada", gastoVariavelId } : l
      ),
    }));
  }, []);

  /* ---------- Reset ---------- */
  const resetAll = useCallback(() => {
    const storageKey = getStorageKey(userId);
    if (storageKey) localStorage.removeItem(storageKey);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    setData(EMPTY);
  }, [userId]);

  /* ---------- Custom Categories ---------- */
  const addCustomCategory = useCallback((cat) => {
    setData((d) => ({
      ...d,
      customCategories: [...(d.customCategories || []), cat],
    }));
  }, []);

  const removeCustomCategory = useCallback((catId) => {
    setData((d) => ({
      ...d,
      customCategories: (d.customCategories || []).filter((c) => c.id !== catId),
    }));
  }, []);

  const value = useMemo(() => ({
    data,
    setSelectedMonth,
    addLancamento, updateLancamento, removeLancamento, removeLancamentoSerie, updateLancamentoSerie, extendLancamentoSerie, truncateLancamentoSerie, deduplicateLancamentos,
    addCartao, updateCartao, removeCartao,
    addCartaoTx, updateCartaoTx, removeCartaoTx, importCartaoCSV, addCartaoTxDirect,
    addObjetivo, updateObjetivo, removeObjetivo,
    addAcao, updateAcao, removeAcao, addCdi, removeCdi, addCrypto, updateCrypto, removeCrypto,
    addCustomCategory, removeCustomCategory,
    addLista, updateLista, removeLista, addItemLista, updateItemLista, removeItemLista, finalizarLista,
    resetAll,
  }), [data, setSelectedMonth, addLancamento, updateLancamento, removeLancamento, removeLancamentoSerie, updateLancamentoSerie, extendLancamentoSerie, truncateLancamentoSerie, deduplicateLancamentos,
    addCartao, updateCartao, removeCartao,
    addCartaoTx, updateCartaoTx, removeCartaoTx, importCartaoCSV, addCartaoTxDirect,
    addObjetivo, updateObjetivo, removeObjetivo,
    addAcao, updateAcao, removeAcao, addCdi, removeCdi, addCrypto, updateCrypto, removeCrypto,
    addCustomCategory, removeCustomCategory,
    addLista, updateLista, removeLista, addItemLista, updateItemLista, removeItemLista, finalizarLista,
    resetAll]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
